import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/overtime/monitoring")({
  beforeLoad: async () => {
    requireSetting("overtime_approval_required");
    await requirePermission("attendance.overtime.view");
  },
  component: OvertimeMonitoringPage,
});

import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SelectUser from "@/components/reusable-components/select-user";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { requireSetting, requirePermission } from "@/lib/feature-gate";

import useFetchOvertimeApprovals from "@/hooks/api/overtime/useFetchOvertimeApprovals";
import { normalizeOvertimeApproval } from "@/hooks/api/overtime/normalizeOvertimeApproval";
import { useCancelOvertimeApproval } from "@/hooks/api/overtime/useOvertimeApprovalActions";
import { OvertimeApprovalTable } from "@/components/supervisor/overtime/OvertimeApprovalTable";
import useAutoCompleteUser from "@/hooks/api/user/useAutoCompleteUser";
import { CancelWithRemarksDialog } from "@/components/crud/ConfirmDialog/CancelWithRemarksDialog";
import { OvertimeRequestDialog } from "@/components/user/overtime/OvertimeRequestDialog";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";
import { toast } from "sonner";
import type { ApprovalRequest, ApprovalStatus } from "@/types/approval";

type StatusFilter = ApprovalStatus | "clear";

interface FilterForm {
  user_id?: number;
}

function OvertimeMonitoringPage() {
  const navigate = useNavigate();
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("clear");
  const [showFilters, setShowFilters] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const { data: capabilities } = useMyPermissions();
  // Filing for someone else is its own capability — holding the monitoring
  // read (attendance.overtime.view) does not imply it.
  const canFile = can(capabilities, "attendance.overtime.manage");

  // Requester autocomplete
  const [searchQuery, setSearchQuery] = useState("");
  const autoCompleteQuery = useAutoCompleteUser({
    search: searchQuery || undefined,
  });
  const users = useMemo(
    () => autoCompleteQuery.data?.pages.flatMap((p) => p.results) ?? [],
    [autoCompleteQuery.data],
  );

  const { watch, setValue, reset } = useForm<FilterForm>({
    defaultValues: { user_id: undefined },
  });
  const watchedUserId = watch("user_id");

  const approvalsQuery = useFetchOvertimeApprovals({
    page,
    page_size: pageSize,
    status: statusFilter !== "clear" ? statusFilter : undefined,
    overtime_request__user: watchedUserId,
  });

  const requests = useMemo(
    () =>
      (approvalsQuery.data?.results ?? []).map(
        (raw) => normalizeOvertimeApproval(raw).request,
      ),
    [approvalsQuery.data],
  );
  const count = approvalsQuery.data?.count ?? 0;

  const cancelMutation = useCancelOvertimeApproval();
  const [cancelTarget, setCancelTarget] = useState<ApprovalRequest | null>(
    null,
  );

  async function handleConfirmCancel(remarks: string) {
    if (!cancelTarget) return;
    try {
      await cancelMutation.mutateAsync({ id: cancelTarget.id, remarks });
      toast.success("Overtime request cancelled");
      setCancelTarget(null);
    } catch {
      // Error toast comes from the mutation's meta.errorMessage
    }
  }

  // Back to page 1 whenever a filter changes.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, watchedUserId, setPage]);

  function resetFilters() {
    setStatusFilter("clear");
    setSearchQuery("");
    reset({ user_id: undefined });
  }

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Overtime"
          description="Monitor overtime requests across the organization."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={canFile ? () => setShowCreate(true) : undefined}
          addLabel="File Overtime"
          isLoading={approvalsQuery.isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={approvalsQuery.isFetching}
        >
          <SelectUser<FilterForm>
            watchedUserId={watchedUserId}
            users={users}
            onSearchUsers={setSearchQuery}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            setValue={setValue}
            isLoading={autoCompleteQuery.isFetching}
            fetchNextPage={autoCompleteQuery.fetchNextPage}
            hasMore={autoCompleteQuery.hasNextPage}
          />

          <SelectFilter
            placeholder="Status"
            options={[
              { label: "Pending", value: "PENDING" },
              { label: "Approved", value: "APPROVED" },
              { label: "Rejected", value: "REJECTED" },
              { label: "Cancelled", value: "CANCELLED" },
              { label: "All", value: "clear" },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </FiltersPanel>

        <OvertimeApprovalTable
          requests={requests}
          loading={approvalsQuery.isPending}
          onViewDetails={(request) =>
            navigate({
              to: "/admin/overtime/monitoring/$id",
              params: { id: String(request.id) },
            })
          }
          onUserClick={(user) =>
            navigate({
              to: "/admin/users/$id",
              params: { id: String(user.id) },
              search: { tab: "overtime" },
            })
          }
          onCancel={(request) => setCancelTarget(request)}
          emptyMessage="No overtime requests found."
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={approvalsQuery.isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </div>

      <OvertimeRequestDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        adminMode
        onSuccess={() => approvalsQuery.refetch()}
      />

      <CancelWithRemarksDialog
        open={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open) setCancelTarget(null);
        }}
        onConfirm={handleConfirmCancel}
        isLoading={cancelMutation.isPending}
        title="Cancel Overtime Request"
        description="This request has already been approved. Cancelling will remove the approved overtime hours from the employee's timesheet. Provide a reason if any."
      />
    </SidebarInset>
  );
}
